import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { athletesAPI } from '../../lib/api';
import { Navbar } from '../../components/Navbar';
import AthleteProfileSwitcher from '../../components/AthleteProfileSwitcher';
import { toast } from 'sonner';
import { Calendar, Euro, FileText, User, LinkIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { downloadPDF } from '../../lib/pdfDownload';

export default function AthleteDashboardPage() {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [reports, setReports] = useState(null);
  const [period, setPeriod] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Verifica se l'atleta ha anche il ruolo coach basandosi SOLO sui dati dell'utente
  const hasDualRole = user?.coach_id || user?.has_coach_role;

  // Aggiorna localStorage basandosi sui dati reali dell'utente
  useEffect(() => {
    if (user) {
      if (user.coach_id || user.has_coach_role) {
        localStorage.setItem('hasDualRole', 'true');
      } else {
        localStorage.removeItem('hasDualRole');
      }
    }
  }, [user]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      const params = period !== 'all' ? { period } : {};
      const [lessonsRes, reportsRes] = await Promise.all([
        athletesAPI.getMyLessons(params),
        athletesAPI.getMyReports(params),
      ]);
      setLessons(lessonsRes.data);
      setReports(reportsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    const coachNames = reports?.by_coach?.length
      ? reports.by_coach.map((coach) => coach.name).join(', ')
      : 'N/A';
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.text("Trainers' Manager - Report Atleta", 14, 20);
    doc.setFontSize(12);
    doc.text(`Allenatore: ${coachNames}`, 14, 28);
    doc.text(`Atleta: ${user?.name}`, 14, 38);
    doc.text(`Periodo: ${period === 'all' ? 'Tutto' : period === 'daily' ? 'Giornaliero' : period === 'weekly' ? 'Settimanale' : 'Mensile'}`, 14, 45);

    if (reports?.by_coach) {
      doc.text('Compensi per Allenatore:', 14, 58);
      let y = 66;
      reports.by_coach.forEach(coach => {
        doc.text(`${coach.name}: Pagato \u20ac${coach.total_paid.toFixed(2)} | Da pagare \u20ac${coach.total_unpaid.toFixed(2)}`, 20, y);
        y += 7;
      });
    }

    if (reports?.by_venue) {
      doc.addPage();
      doc.text('Affitti per Struttura:', 14, 20);
      doc.text(`Allenatore: ${coachNames}`, 14, 28);
      let y = 36;
      reports.by_venue.forEach(venue => {
        doc.text(`${venue.name}: Pagato \u20ac${venue.total_paid.toFixed(2)} | Da pagare \u20ac${venue.total_unpaid.toFixed(2)}`, 20, y);
        y += 7;
      });
    }

    await downloadPDF(doc, `report-atleta-${period}.pdf`, {
      title: 'Report atleta',
      text: 'Report atleta pronto',
    });
    toast.success('Report pronto!');
  };

  const totalPaid = lessons.reduce((sum, l) => {
    const coachPaid = l.coach_payment_status === 'paid' ? (l.coach_amount || 0) : 0;
    const venuePaid = l.venue_payment_status === 'paid' ? (l.venue_amount || 0) : 0;
    return sum + coachPaid + venuePaid;
  }, 0);
  
  const totalUnpaid = lessons.reduce((sum, l) => {
    const coachUnpaid = l.coach_payment_status !== 'paid' ? (l.coach_amount || 0) : 0;
    const venueUnpaid = l.venue_payment_status !== 'paid' ? (l.venue_amount || 0) : 0;
    return sum + coachUnpaid + venueUnpaid;
  }, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-[#ccff00] text-xl font-bold">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="app-shell bg-[#09090b]">
      <Navbar />

      <main className="app-scroll has-top-bar">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-1 pb-4 sm:pt-2 sm:pb-8 md:pt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl font-black text-white mb-1 sm:mb-2">La Mia Dashboard</h1>
            <p className="text-zinc-400 text-sm sm:text-base">Visualizza le tue lezioni e i pagamenti</p>
            <div className="mt-2">
              <AthleteProfileSwitcher />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-full sm:w-[180px] bg-zinc-900 border-zinc-800 rounded-xl h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">Tutto</SelectItem>
                <SelectItem value="daily">Giornaliero</SelectItem>
                <SelectItem value="weekly">Settimanale</SelectItem>
                <SelectItem value="monthly">Mensile</SelectItem>
              </SelectContent>
            </Select>

            <Button
              onClick={exportPDF}
              className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl h-10 text-sm"
            >
              <FileText className="w-4 h-4 mr-2" />
              Esporta PDF
            </Button>
          </div>
        </div>

        {!user?.coach_id && (
          <div className="glass-card p-3 sm:p-4 rounded-xl border border-[#ccff00]/30 bg-[#ccff00]/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 sm:mb-4">
            <div className="flex items-start gap-2">
              <LinkIcon className="w-4 h-4 text-[#ccff00] mt-0.5 flex-shrink-0" />
              <p className="text-zinc-300 text-xs sm:text-sm">
                Non vedi le lezioni del tuo allenatore? Hai un codice atleta da lui? Collega il profilo dalla pagina "Profilo".
              </p>
            </div>
            <Button
              onClick={() => navigate('/athlete/profile')}
              className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl h-9 text-xs sm:text-sm whitespace-nowrap flex-shrink-0 self-start sm:self-auto"
            >
              Collega profilo
            </Button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-0.5 sm:gap-1 mb-2 sm:mb-3">
          <div 
            onClick={() => scrollToSection('lessons')}
            className="glass-card p-3 sm:p-6 rounded-xl border border-white/10 cursor-pointer hover:border-[#ccff00]/30 transition-all hover:scale-[1.02]"
          >
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <Calendar className="w-6 h-6 sm:w-10 sm:h-10 text-[#ccff00]" />
              <div className="text-xl sm:text-3xl font-black text-white">{lessons.length}</div>
            </div>
            <div className="text-zinc-400 text-xs sm:text-sm font-medium">Lezioni Totali</div>
          </div>

          <div 
            onClick={() => scrollToSection('lessons')}
            className="glass-card p-3 sm:p-6 rounded-xl border border-white/10 cursor-pointer hover:border-green-500/30 transition-all hover:scale-[1.02]"
          >
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <Euro className="w-6 h-6 sm:w-10 sm:h-10 text-green-500" />
              <div className="text-xl sm:text-3xl font-black text-white">€{totalPaid.toFixed(2)}</div>
            </div>
            <div className="text-zinc-400 text-xs sm:text-sm font-medium">Totale Pagato</div>
          </div>

          <div 
            onClick={() => scrollToSection('lessons')}
            className="glass-card p-3 sm:p-6 rounded-xl border border-white/10 cursor-pointer hover:border-red-500/30 transition-all hover:scale-[1.02] col-span-2 md:col-span-1"
          >
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <Euro className="w-6 h-6 sm:w-10 sm:h-10 text-red-500" />
              <div className="text-xl sm:text-3xl font-black text-white">€{totalUnpaid.toFixed(2)}</div>
            </div>
            <div className="text-zinc-400 text-xs sm:text-sm font-medium">Da Pagare</div>
          </div>
        </div>

        {reports && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0.5 sm:gap-1 mb-2 sm:mb-3">
            <div className="glass-card p-4 sm:p-6 rounded-xl border border-white/10">
              <h2 className="text-lg sm:text-2xl font-bold text-white mb-3 sm:mb-4">Compensi per Allenatore</h2>
              <div className="space-y-2 sm:space-y-3">
                {reports.by_coach.map((coach, idx) => (
                  <div key={idx} className="p-3 sm:p-4 bg-zinc-900/50 rounded-xl border border-white/10">
                    <div className="font-bold text-white mb-2 text-sm sm:text-base">{coach.name}</div>
                    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                      <div>
                        <span className="text-zinc-400">Pagato:</span>
                        <span className="text-green-500 font-mono ml-1 sm:ml-2">€{coach.total_paid.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-zinc-400">Da pagare:</span>
                        <span className="text-red-500 font-mono ml-1 sm:ml-2">€{coach.total_unpaid.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-card p-4 sm:p-6 rounded-xl border border-white/10">
              <h2 className="text-lg sm:text-2xl font-bold text-white mb-3 sm:mb-4">Affitti per Struttura</h2>
              <div className="space-y-2 sm:space-y-3">
                {reports.by_venue.map((venue, idx) => (
                  <div key={idx} className="p-3 sm:p-4 bg-zinc-900/50 rounded-xl border border-white/10">
                    <div className="font-bold text-white mb-2 text-sm sm:text-base">{venue.name}</div>
                    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                      <div>
                        <span className="text-zinc-400">Pagato:</span>
                        <span className="text-green-500 font-mono ml-1 sm:ml-2">€{venue.total_paid.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-zinc-400">Da pagare:</span>
                        <span className="text-red-500 font-mono ml-1 sm:ml-2">€{venue.total_unpaid.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div id="lessons" className="glass-card p-4 sm:p-6 rounded-xl border border-white/10">
          <h2 className="text-lg sm:text-2xl font-bold text-white mb-3 sm:mb-4">Le Mie Lezioni</h2>
          {lessons.length === 0 ? (
            <p className="text-zinc-400 text-sm">Nessuna lezione disponibile</p>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {lessons.map((lesson) => (
                <div
                  key={lesson.lesson_id}
                  className={`p-3 sm:p-4 rounded-xl border ${
                    lesson.coach_payment_status === 'paid' && lesson.venue_payment_status === 'paid'
                      ? 'bg-green-900/20 border-green-700/30'
                      : 'bg-zinc-900/50 border-white/10'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-1 sm:mb-2">
                        <span className="text-white font-bold text-sm sm:text-base">
                          {new Date(lesson.date).toLocaleDateString('it-IT')}
                        </span>
                        <span className="text-zinc-400 text-sm">{lesson.start_time}</span>
                        <span className="text-zinc-400 text-sm">({lesson.duration_minutes} min)</span>
                      </div>
                      <div className="text-xs sm:text-sm text-zinc-400">
                        <span className="block sm:inline">Allenatore: {lesson.coach_name}</span>
                        <span className="hidden sm:inline"> | </span>
                        <span className="block sm:inline">Struttura: {lesson.venue_name}</span>
                      </div>
                    </div>
                    <div className="text-left sm:text-right space-y-1">
                      <div className="flex items-center sm:justify-end space-x-2">
                        <span className="text-zinc-400 text-xs">Allenatore:</span>
                        <span className={`font-mono text-xs sm:text-sm ${
                          lesson.coach_payment_status === 'paid' ? 'text-green-500' : 'text-red-500'
                        }`}>€{lesson.coach_amount?.toFixed(2) || '0.00'}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-lg ${
                          lesson.coach_payment_status === 'paid' ? 'bg-green-900/30 text-green-500' : 'bg-red-900/30 text-red-500'
                        }`}>
                          {lesson.coach_payment_status === 'paid' ? '✓' : '✗'}
                        </span>
                      </div>
                      <div className="flex items-center sm:justify-end space-x-2">
                        <span className="text-zinc-400 text-xs">Struttura:</span>
                        <span className={`font-mono text-xs sm:text-sm ${
                          lesson.venue_payment_status === 'paid' ? 'text-green-500' : 'text-red-500'
                        }`}>€{lesson.venue_amount?.toFixed(2) || '0.00'}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-lg ${
                          lesson.venue_payment_status === 'paid' ? 'bg-green-900/30 text-green-500' : 'bg-red-900/30 text-red-500'
                        }`}>
                          {lesson.venue_payment_status === 'paid' ? '✓' : '✗'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </main>
    </div>
  );
}
